import { useFormContext } from 'react-hook-form';
import { OfferCategory, isDeviceEppSubcategory } from '../../../../common/enums.js';
import { monthlyChargesMsg, singleFaresMsg, t, totalMsg, vatPercentageMsg } from '../../../../common/i18n/index.js';
import { formatSumToString } from '../../../../common/utils/priceUtils.js';
import { getLocalizedString } from '../../../../common/utils/productOfferingsUtils.js';
import { getPriceWithTax, getTaxAmount, getVatAsString } from '../../../../common/utils/taxUtils.js';
import {
  calculateChargeTotals,
  getAddonsForOrderSummary,
  getPriceDataForAddon,
  getPriceDataForBasketItem,
} from '../../../ShoppingBasket/shoppingBasketHelpersCoCo.js';
import { PlaceOrderSummaryViewCoCo } from './PlaceOrderSummaryViewCoCo.js';
import type { DetailedSummaryData, PlaceOrderSummaryViewCoCoProps, TableRow } from './PlaceOrderSummaryViewCoCo.js';
import type { ProductOfferingCartItems, ShoppingBasketType } from '../../../../common/types/shoppingBasketCoCo.js';
import type { CartProductOfferingCoco } from '../../../../generated/api/cartProductOfferingCoco.js';
import type { PriceData } from '../../../ShoppingBasket/types.js';
import type { PlaceOrderFormData } from '../../placeOrderTypes.js';

const pcs = (n: number) => t.BDBD('{} pcs', String(n));

export interface PlaceOrderSummaryProps {
  shoppingBasket: ShoppingBasketType;
  defaultOpen?: boolean;
}

export const PlaceOrderSummaryCoCo = ({ shoppingBasket, defaultOpen }: PlaceOrderSummaryProps) => {
  const methods = useFormContext<PlaceOrderFormData>();

  const { monthlyRecurringCharges, onetimeCharges } = calculateChargeTotals(shoppingBasket.productOfferings);

  const deliveryPriceInCents = methods.watch('delivery.deliveryPriceInCents') ?? 0;

  const totalOnetimeCharges = onetimeCharges + deliveryPriceInCents;

  const onetimeCell = ({ price }: PriceData, quantity = 1) =>
    price?.oneTimeCharge === undefined ? undefined : formatSumToString(price.oneTimeCharge * quantity);

  const monthlyCell = ({ price }: PriceData, quantity = 1) =>
    price?.monthlyRecurringCharge === undefined
      ? undefined
      : t.YO7F('{}/Month', formatSumToString(price.monthlyRecurringCharge * quantity));

  const mapAddonRow = (
    cartItem: ProductOfferingCartItems,
    addon: CartProductOfferingCoco,
    quantity: number
  ): TableRow => {
    const priceData = getPriceDataForAddon(addon);
    return {
      rowHeader: false,
      indented: true,
      key: `${cartItem.cartItemId}_addon_${addon.id}`,
      name: getLocalizedString(addon.name),
      amount: pcs(quantity),
      onetime: onetimeCell(priceData, quantity),
      monthly: monthlyCell(priceData, quantity),
    };
  };

  const mapMainRow = (cartItem: ProductOfferingCartItems, priceData: PriceData, quantity: number): TableRow => ({
    rowHeader: true,
    key: cartItem.cartItemId,
    name: getLocalizedString(cartItem.name),
    amount: pcs(quantity),
    onetime: onetimeCell(priceData, quantity),
    monthly: monthlyCell(priceData, quantity),
  });

  const getSummaryRows = (): TableRow[] => {
    const groups = new Map<string, ProductOfferingCartItems[]>();
    (shoppingBasket.productOfferings ?? []).forEach((cartItem, index) => {
      const isDevice =
        isDeviceEppSubcategory(cartItem.productOfferingType?.subcategory) ||
        cartItem.productOfferingType?.category === OfferCategory.MOBILE_DEVICE;
      const key = isDevice ? JSON.stringify([cartItem.id, cartItem.productOfferingPrice ?? []]) : `entry-${index}`;
      const group = groups.get(key) ?? [];
      group.push(cartItem);
      groups.set(key, group);
    });

    return Array.from(groups.values()).flatMap(group => {
      const cartItem = group[0];
      const addonGroups = new Map<string, { addon: CartProductOfferingCoco; quantity: number }>();
      group.flatMap(getAddonsForOrderSummary).forEach(addon => {
        const key = JSON.stringify([addon.id, addon.productOfferingPrice ?? []]);
        const existing = addonGroups.get(key);
        addonGroups.set(key, { addon, quantity: (existing?.quantity ?? 0) + 1 });
      });

      return [
        mapMainRow(cartItem, getPriceDataForBasketItem(cartItem), group.length),
        ...Array.from(addonGroups.values()).map(({ addon, quantity }, index) => ({
          ...mapAddonRow(cartItem, addon, quantity),
          key: `${cartItem.cartItemId}_addon_${addon.id}_${index}`,
        })),
      ];
    });
  };

  const oneTimeVat = formatSumToString(getTaxAmount(onetimeCharges));
  const monthlyVat = t.YO7F('{}/Month', formatSumToString(getTaxAmount(monthlyRecurringCharges)));

  const simpleSummary: PlaceOrderSummaryViewCoCoProps['simpleSummary'] = {
    totalHeading: t.CEQ2(totalMsg),
    monthlyLabel: t.P6BC(monthlyChargesMsg),
    monthlyValue: t.YO7F('{}/Month', formatSumToString(monthlyRecurringCharges)),
    monthlyVat: `+ ${t.UZ6X('VAT')} ${monthlyVat}`,
    monthlyCharges: monthlyRecurringCharges,
    oneTimeLabel: t.GOBY(singleFaresMsg),
    oneTimeValue: formatSumToString(totalOnetimeCharges),
    oneTimeVat: `+ ${t.UZ6X('VAT')} ${oneTimeVat}`,
    oneTimeCharges: totalOnetimeCharges,
  };

  const detailed: DetailedSummaryData = {
    rows: [
      ...getSummaryRows(),
      {
        key: 'deliveryCharge',
        rowHeader: true,
        name: t.PFDS('Delivery fee'),
        onetime: formatSumToString(deliveryPriceInCents),
        cssClass: 'delivery-charge',
      },
      {
        key: 'total',
        rowHeader: true,
        name: t.CEQ2(totalMsg),
        onetime: <span className="of-order-summary-row-bolded">{formatSumToString(totalOnetimeCharges)}</span>,
        monthly: (
          <span className="of-order-summary-row-bolded">
            {t.YO7F('{}/Month', formatSumToString(monthlyRecurringCharges))}
          </span>
        ),
        cssClass: 'total',
      },
    ],
    footer: [
      {
        key: 'totalVat',
        name: t.A0OJ(vatPercentageMsg, getVatAsString()),
        onetime: oneTimeVat,
        monthly: monthlyVat,
        cssClass: 'total-vat',
      },
      {
        key: 'totalWithTax',
        name: t.YXMI('Price including taxes'),
        onetime: formatSumToString(getPriceWithTax(totalOnetimeCharges)),
        monthly: t.YO7F('{}/Month', formatSumToString(getPriceWithTax(monthlyRecurringCharges))),
        cssClass: 'total-with-tax',
      },
    ],
  };

  return (
    <PlaceOrderSummaryViewCoCo
      simpleSummary={simpleSummary}
      detailed={detailed}
      defaultOpen={defaultOpen}
      toggleLabels={{
        hideDetails: t.TQNV('Hide details'),
        showDetails: t.CUQU('Show details'),
      }}
    />
  );
};
